import { useContext, useEffect } from 'react';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import parse from 'autosuggest-highlight/parse';
import { FormAutocomplete, FormAutocompleteProps, useForm, useGoogleMaps } from '@hilma/netina2all';
import { LocationContext } from '../context';
import { useI18n } from '../hooks';

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;

const autocompleteService: { current: AutocompleteService | null } = { current: null };

interface LocationAutocompleteDropdownProps {
	description?: string;
	classes?: FormAutocompleteProps<AutocompletePrediction>['classes'];
}

const LocationAutocompleteDropdown: React.FC<LocationAutocompleteDropdownProps> = ({ description, classes: cl }) => {
	const locationC = useContext(LocationContext)!
	const stationReg = useI18n(i18n => i18n.stationReg);
	const isLoading = useGoogleMaps();
	const classes = useStyles();
	const form = useForm();

	const fetchPerdictions = async (input: string) => {
		if (!input) return [];
		if (!autocompleteService.current && window.google) {
			autocompleteService.current = new window.google.maps.places.AutocompleteService();
		}
		const perdictions = await new Promise<AutocompletePrediction[]>(reslove => autocompleteService.current!.getPlacePredictions({ input }, reslove));
		return perdictions || [];
	}

	useEffect(() => {
		return () => locationC?.setValue2(form.values.address);
	}, [locationC, form]);

	return (
		<FormAutocomplete
			key={locationC?.value2?.description}
			id="address"
			autoComplete={false}
			isLoading={isLoading}
			description={description ?? stationReg.labels.address}
			noOptionsText={stationReg.noOptions}
			initialValue={locationC?.value2 || form.values?.address}
			fetchFunction={fetchPerdictions}
			getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
			classes={cl}
			renderOption={(option) => {
				const matches = option?.structured_formatting?.main_text_matched_substrings || [];
				const parts = parse(
					option.structured_formatting.main_text,
					matches.map((match: any) => [match.offset, match.offset + match.length]),
				);
				return (
					<Grid container alignItems="center" >
						<Grid item>
							<LocationOnIcon className={classes.icon} />
						</Grid>
						<Grid item xs>
							{parts.map((part: { text: string; highlight: boolean }, index: number) => (
								<span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
									{part.text}
								</span>
							))}
							<Typography variant="body2" color="textSecondary">
								{option.structured_formatting.secondary_text}
							</Typography>
						</Grid>
					</Grid>
				);
			}}
		/>
	);
}

export default LocationAutocompleteDropdown;

const useStyles = makeStyles(theme => ({
	icon: {
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(2),
	}
}));
